import React, { FC, useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import {
  GenericResponse,
  CrmUserNewPasswordRequestDto,
  parseJwt,
  CrmUserResetPasswordUpdateTokenResponseDto,
  CrmUserResetPasswordValidateTokenRequestDto,
  CrmUserResetPasswordValidateTokenResponseDto,
} from '@sparling/financial-common'
import { toast } from 'react-toastify'
import { Grid, Typography } from '@mui/material'
import { useRest } from '../../components/Context'
import { Loader } from '../../components/Loader'
import { ResetPasswordForm } from './ResetPasswordForm'
import useStyles from './useStyle'
import { routes } from '../../constants/routes'
import { OutlinedButton } from '../../components/Buttons/OutlinedButton'

interface UserTokenData {
  email: string
  firstName: string
  lastName: string
}

export const ResetPassword: FC = (): JSX.Element => {
  const classes = useStyles()
  const { rxios } = useRest()
  const { userIdReference } = useParams() as { [key: string]: string }
  const { search } = useLocation()
  const stringParams = new URLSearchParams(search)
  const token = stringParams.get('token')
  const [entityResetPassword, setEntityResetPassword] = useState<CrmUserNewPasswordRequestDto>(
    new CrmUserNewPasswordRequestDto(),
  )
  const [isLinkValid, setIsLinkValid] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [isPasswordChanged, setIsPasswordChanged] = useState<boolean>(false)

  useEffect(() => {
    setLoading(true)
    if (token) {
      const dataToken: UserTokenData = parseJwt(token)
      if (dataToken) {
        setEntityResetPassword({
          password: '',
          confirmPassword: '',
        })
        const data: CrmUserResetPasswordValidateTokenRequestDto = {
          userIdReference,
          token,
        }
        rxios.post('password/reset/validate', data).subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setLoading(false)
            const result: GenericResponse<CrmUserResetPasswordValidateTokenResponseDto> = next
            if (result && result.data.isValid) {
              setIsLinkValid(true)
            } else {
              setIsLinkValid(false)
              toast.error('Provided link is expired')
            }
          },
          () => {
            setLoading(false)
            setIsLinkValid(false)
            toast.error('Provided link is expired')
          },
        )
      }
    }
  }, [rxios, token, userIdReference])

  const onSubmit = useCallback(
    // eslint-disable-next-line
    (newValue: any) => {
      if (newValue.password === newValue.confirmPassword) {
        setLoading(true)
        rxios
          .put('password', {
            userIdReference,
            token,
            password: newValue.password,
          })
          .subscribe(
            // eslint-disable-next-line
            (next: any) => {
              const result: GenericResponse<CrmUserResetPasswordUpdateTokenResponseDto> = next
              setLoading(false)
              if (result && result.data.result) {
                setIsPasswordChanged(true)
              } else {
                setIsPasswordChanged(false)
                toast.error('Can not change password')
              }
            },
            () => {
              setLoading(false)
              setIsPasswordChanged(false)
              toast.error('Can not change password')
            },
          )
      }
    },
    [rxios, token, userIdReference],
  )

  return (
    <main className={classes.main}>
      <Grid container className={classes.containerForm}>
        {loading && <Loader />}
        {isLinkValid && !isPasswordChanged && <ResetPasswordForm onSubmit={onSubmit} model={entityResetPassword} />}

        {!isLinkValid && !isPasswordChanged && !loading && (
          <Grid container direction="column" alignItems="center">
            <Grid container justifyContent={'center'} alignItems={'center'}>
              <Typography variant="h2" gutterBottom>
                Reset Password
              </Typography>
            </Grid>
            <Grid container justifyContent={'center'} alignItems={'center'}>
              <Typography variant={'h6'}>Provided link is expired or not valid.</Typography>
            </Grid>
          </Grid>
        )}
        {isLinkValid && isPasswordChanged && (
          <Grid className={classes.containerForm} container direction="column" alignItems="center">
            <Grid container justifyContent={'center'} alignItems={'center'}>
              <Typography variant="h2" gutterBottom>
                Reset Password
              </Typography>
            </Grid>
            <Grid container justifyContent={'center'} alignItems={'center'}>
              <Grid container justifyContent={'center'} alignItems={'center'}>
                <Typography variant={'h6'}>Password was changed successfully!</Typography>
              </Grid>
            </Grid>
            <OutlinedButton width="100%" mt={42} href={routes.LOGIN} text="Back to Login" />
          </Grid>
        )}
      </Grid>
    </main>
  )
}
